import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'

/**
 * Some processes execute the same steps per some item. For example, when
 * importing into the DMS, we have to insert records for each DMS entity. In
 * that case, we want to display the importing of each entity as its own step.
 * E.g., step 5: Import Buildings, step 6: Import Rooms, etc.
 *
 * This represents a particular item, of a particular {@link Item.Type}
 */
export interface Item extends Item.Reference, common.Named {
  // NOTE: name is for templates to use when getting concrete steps
}
export namespace Item {
  /**
   * Identifies an {@link Item}
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<string> = TypeChecker.isString
  }
  /**
   * TODO: explain what this enum is, when it is used and why, as well as any examples
   *
   * TODO: add JSDoc comments to each enum value to explain it
   */
  export enum Type {
    RecordModel = 'record-model',
    Pool = 'pool',
    Allocation = 'allocation',
    Report = 'report',
    /** TODO: ... */
    Importer = 'importer',
    /**
     * Fallback value for cases where an unknown or removed enum value is
     * encountered. This handles scenarios where:
     * - An enum type is modified, and previously stored values in the database
     *   become invalid.
     * - The application receives an undefined or unexpected enum value. Using this
     *   value prevents errors and ensures graceful handling of such cases.
     */
    Unknown = 'unknown',
  }
  // Needs to be before namespace is defined
  const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
  export namespace Type {
    export const ALL: readonly Type[] = Array.from(TYPES)
    export const isType = TypeChecker.Factory.oneOf(TYPES)
    export const assertIsType: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isType)
  }

  /**
   * Identifies an {@link Item}, without including all of the details about the
   * item
   */
  export interface Reference extends common.Identified<Item.Id> {
    type: Item.Type
  }
  export namespace Reference {
    export const isReference: TypeChecker<Reference> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          common.Identified.getTypeChecker(Id.isId),
          TypeChecker.Factory.object<
            Difference<Reference, common.Identified<Item.Id>>
          >({
            type: Item.Type.isType,
          }),
        ),
    )
    /**
     * Determines if two {@link Reference} are pointing to the same {@link Item}
     */
    export function isEqual(
      ref1: Readonly<Reference> | undefined,
      ref2: Readonly<Reference> | undefined,
    ): boolean {
      return (
        // Straight equality check is faster, in case they reference the same
        // object, but stringify is more complete
        ref1 === ref2 || (ref1 && stringify(ref1)) === (ref2 && stringify(ref2))
      )
    }

    /**
     * Represents a {@link Reference} as a string
     */
    export function stringify({ id, type }: Readonly<Reference>): string {
      return `${type}:${id.toString()}`
    }
  }
}
